import React, {
  useEffect, useMemo, useState, Key,
} from 'react';
import classNames from 'classnames';
import { Button, Radio, Table as AntdTable, TableProps as AntdTableProps } from 'antd';
import { SettingOutlined } from '@ant-design/icons';

import { Checkbox } from 'components/Controls/Checkbox';
import { Tooltip } from 'components/Tooltip';

import { filterConfig } from 'components/Table/utils';

import { ColumnType } from './types';
import { withPinIndicators } from './pin-indicator';
import cn from './styles.module.scss';

export type { ColumnType } from './types';
export * from './settings';

export enum OrderMap {
  ascend = 'ASC',
  descend = 'DESC',
}

function isReactElement(node: React.ReactNode): node is React.ReactElement {
  return React.isValidElement(node);
}

export type TableProps<RecordType> = AntdTableProps<RecordType> & {
  columns: ColumnType<RecordType>[],
  onSortingChange?: (newSorting: { [key: string]: OrderMap }) => any,
  onSettingsClick?: React.MouseEventHandler,
  pagination?: boolean | any,
  tableClassName?: string,
  settingsButtonTestId?: string,
  /** Show pin glyphs on the headers of pinned (`fixed: 'left'`) columns.
   *  Default false — enable per table as it migrates to Table Settings v2, so
   *  unmigrated tables render exactly as before. */
  showPinIndicators?: boolean,
};

export function Table<RecordType extends { [key: string]: any } = any>(
  {
    columns: columnsProp,
    tableLayout,
    dataSource,
    onSortingChange,
    rowSelection,
    rowKey = 'id',
    pagination = true,
    bordered = true,
    expandable,
    onSettingsClick,
    tableClassName,
    className,
    settingsButtonTestId,
    showPinIndicators = false,
    rootClassName,
    ...restProps
  }: TableProps<RecordType>,
) {
  const [expandedRows, setExpandedRows] = useState<readonly Key[]>([]);

  const pageDataLength = dataSource?.length || 0;

  const paginationConfig = useMemo(() => {
    if (typeof pagination === 'boolean') {
      return pagination ? { className: cn.pagination } : false;
    }

    return {
      total: pageDataLength && (pageDataLength % pagination.pageSize === 0)
        ? pagination.current * pagination.pageSize + 1
        : (pagination.current - 1) * pagination.pageSize + Math.max(pageDataLength, 1),
      showSizeChanger: true,
      showLessItems: true,
      pageSizeOptions: [10, 25, 50],
      className: cn.pagination,
      ...pagination,
      onChange: pagination.onChange ? (page: number, pageSize: number) => {
        pagination.onChange(
          pageSize === pagination.pageSize ? page : 1,
          pageSize,
        );
      } : undefined,
    };
  }, [pagination, pageDataLength]);

  const columns = useMemo(
    () => (showPinIndicators ? withPinIndicators(filterConfig(columnsProp)) : filterConfig(columnsProp)),
    [columnsProp, showPinIndicators]
  );

  const handleChange: NonNullable<AntdTableProps<RecordType>['onChange']> = (
    pagination,
    filters,
    sorter,
    extra,
  ) => {
    if (!onSortingChange || extra.action !== 'sort') {
      return;
    }

    const sorters = Array.isArray(sorter) ? sorter : [sorter];

    onSortingChange(
      sorters.reduce<{ [key: string]: OrderMap }>((result, { columnKey, order }) => {
        if (columnKey != null && order) {
          result[String(columnKey)] = OrderMap[order];
        }
        return result;
      }, {}),
    );
  };

  const onExpandedRowsChange = (keys: readonly Key[]) => {
    setExpandedRows(keys);
  };

  useEffect(() => {
    if (expandable && expandable.expandedRowKeys) {
      setExpandedRows(expandable.expandedRowKeys);
    }
  }, [expandable]);

  return (
    <div className={classNames(cn.tableContainer, className)}>
      <AntdTable
        size="medium"
        bordered={bordered}
        className={tableClassName}
        tableLayout={tableLayout}
        columns={columns}
        dataSource={dataSource}
        rowKey={rowKey}
        pagination={paginationConfig}
        onChange={handleChange}
        rootClassName={classNames(cn.tableRoot, rootClassName)}
        expandable={expandable && {
          onExpandedRowsChange,
          ...expandable,
          expandedRowClassName: classNames(cn.expandedRow, expandable.expandedRowClassName),
          expandedRowRender: (record, index, indent, expanded) => {
            if (expanded && expandable.expandedRowRender) {
              return expandable.expandedRowRender(record, index, indent, expanded);
            }

            return null;
          },
        }}
        rowSelection={
          rowSelection ? {
            ...rowSelection,
            renderCell: function renderSelection(checked, record, index, node) {
              if (rowSelection.type === 'radio') {
                return <Radio {...(isReactElement(node) ? node.props : {})} checked={checked} />;
              }

              return (
                <Checkbox {...(isReactElement(node) ? node.props : {})} checked={checked} />
              );
            },
          } : undefined
        }
        rowClassName={(record) => (expandedRows.includes(record.id) ? cn.expandedRow : '')}
        {...restProps}
      />
      {onSettingsClick && (
        <div className={cn.settingButton}>
          <Tooltip placement="left" title="Edit Display Field">
            <Button
              type="text"
              data-testid={settingsButtonTestId}
              onClick={onSettingsClick}
              icon={<SettingOutlined />}
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
}
